import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  PROBLEM_IMPACT_LABELS,
  PROBLEM_IMPACTS_WORST_FIRST,
  ProblemImpact,
  ProblemReport,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getCurrentClientName } from '@paragon/login-tools';
import { enqueueError, enqueueSuccess } from '@paragon/notification-tools';

import { createScrollbarStyling } from '../createScrollbarStyling';
import { useAppWithThemeSelector } from './hooks';

/**
 * Widest edge we'll send. Captures come out of the viewport at `window.devicePixelRatio`, so a 4K or Retina display
 * produces an image several times larger than the CSS size suggests — big enough to matter on a slow connection for
 * no diagnostic gain.
 */
const MAX_SCREENSHOT_EDGE_IN_PIXELS = 1600;

export interface ReportContext {
  /**
   * Human-readable version of the page address, e.g. "Lot 208 Girder G3 · selected: Member". The report always carries
   * `window.location.href`, but in Design that address is all GUIDs.
   */
  readonly description?: string;
  readonly clientVersion?: string;
}

interface Props {
  readonly onClose: () => void;
  readonly reportContext?: ReportContext;
  readonly getReportScreenshot?: () => string | null;
}

/**
 * Shrinks a PNG data URL so its longest edge is at most `MAX_SCREENSHOT_EDGE_IN_PIXELS`. Returns the original on any
 * failure — a screenshot that won't downscale is still worth sending.
 */
function downscaleScreenshot(pngDataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const image = new Image();

    image.onload = () => {
      const longestEdge = Math.max(image.width, image.height);
      if (longestEdge <= MAX_SCREENSHOT_EDGE_IN_PIXELS) {
        resolve(pngDataUrl);
        return;
      }

      const scale = MAX_SCREENSHOT_EDGE_IN_PIXELS / longestEdge;

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);

      const canvasContext = canvas.getContext('2d');
      if (!canvasContext) {
        resolve(pngDataUrl);
        return;
      }

      canvasContext.drawImage(image, 0, 0, canvas.width, canvas.height);

      try {
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(pngDataUrl);
      }
    };

    image.onerror = () => resolve(pngDataUrl);
    image.src = pngDataUrl;
  });
}

export function ReportProblemDialog({ onClose, reportContext, getReportScreenshot }: Props) {
  const dispatch = useDispatch();

  const user = useAppWithThemeSelector((state) => state.logInData.loggedInUser!);
  const environment = useAppWithThemeSelector((state) => state.environment.activeEnvironment);
  const clientName = useAppWithThemeSelector(getCurrentClientName);

  const [goal, setGoal] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [impact, setImpact] = useState<ProblemImpact | ''>('');

  const [isEditingIdentity, setIsEditingIdentity] = useState(false);
  const [reportedName, setReportedName] = useState(`${user.firstName} ${user.lastName}`.trim());
  const [replyToEmail, setReplyToEmail] = useState(user.email);

  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [showCapturedContext, setShowCapturedContext] = useState(false);

  const [isValidating, setIsValidating] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const goalFieldRef = useRef<HTMLInputElement>(null);
  const whatHappenedFieldRef = useRef<HTMLInputElement>(null);

  const { description: whatIsOpen, clientVersion } = reportContext ?? {};

  // The address is read once, when the dialog opens, so it matches what the customer was looking at when they decided
  //   to report rather than wherever they end up after typing. `reportContext` needs no such care: it comes from the
  //   store, so it cannot change underneath a modal dialog.
  const [pageUrl] = useState(() => window.location.href);
  const [screenshot, setScreenshot] = useState<string | null>(null);

  useEffect(() => {
    const captured = getReportScreenshot?.() ?? null;
    if (captured == null) return;

    let isStillMounted = true;
    downscaleScreenshot(captured).then((downscaled) => {
      if (isStillMounted) setScreenshot(downscaled);
    });

    return () => {
      isStillMounted = false;
    };
    // Capture is a one-shot on open; re-running it on every render would re-render the 3D scene needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const capturedContextRows: readonly (readonly [string, string])[] = [
    ['Your name', reportedName],
    ['Email', user.email],
    ['Organization', user.organization],
    ['Reply to', replyToEmail === user.email ? user.email : `${replyToEmail} (instead of your account email)`],
    ['App', [clientName ?? 'Paragon', clientVersion && `version ${clientVersion}`].filter(Boolean).join(', ')],
    ['Environment', environment],
    ['Page address', pageUrl],
    ...(whatIsOpen ? ([['What you have open', whatIsOpen]] as const) : []),
    ['Browser', describeBrowser()],
    ['Window size', `${window.innerWidth} × ${window.innerHeight}`],
    ['Your local time', describeNow()],
  ];

  function onSubmit() {
    if (goal.trim() === '') {
      setIsValidating(true);
      goalFieldRef.current?.focus();
      return;
    }

    if (whatHappened.trim() === '') {
      setIsValidating(true);
      whatHappenedFieldRef.current?.focus();
      return;
    }

    if (impact === '') {
      setIsValidating(true);
      return;
    }

    setIsSending(true);

    const report: ProblemReport = {
      goal: goal.trim(),
      whatHappened: whatHappened.trim(),
      impact,

      reportedName: reportedName.trim() === '' ? null : reportedName.trim(),
      replyToEmail: replyToEmail.trim() === '' || replyToEmail.trim() === user.email ? null : replyToEmail.trim(),

      url: pageUrl,
      contextDescription: whatIsOpen ?? null,

      clientName: clientName ?? 'Unknown',
      environment,
      clientVersion: clientVersion ?? null,

      browserDescription: describeBrowser(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      localTimestamp: describeNow(),

      screenshotPngDataUrl: includeScreenshot ? screenshot : null,
    };

    designService.support
      .reportProblem(report)
      .then(() => {
        dispatch(enqueueSuccess(`Thanks — we've got it. We'll follow up at ${replyToEmail}.`));
        onClose();
      })
      .catch((error) => {
        console.error(error);
        setIsSending(false);
        dispatch(
          enqueueError(
            'We could not send your report. Please try again, or email help@paragontruss.com if it keeps failing.',
          ),
        );
      });
  }

  return (
    <Dialog open onClose={isSending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Report a Problem</DialogTitle>

      <DialogContent>
        <DialogContentText gutterBottom>
          Tell us what you were trying to accomplish and what got in the way. The more we know about your problem, the
          better we can support you.
        </DialogContentText>

        <Paper variant="outlined" sx={{ padding: 1.5, marginY: 2 }}>
          {isEditingIdentity ? (
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Your name"
                value={reportedName}
                onChange={(event) => setReportedName(event.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Send our reply to"
                helperText="Use a different address if someone else should receive the response."
                value={replyToEmail}
                onChange={(event) => setReplyToEmail(event.target.value)}
              />
              <Box>
                <Button size="small" onClick={() => setIsEditingIdentity(false)}>
                  Done
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Reporting as
                </Typography>
                <Typography variant="body2">{reportedName}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {replyToEmail}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {user.organization}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setIsEditingIdentity(true)}>
                Edit
              </Button>
            </Stack>
          )}
        </Paper>

        <TextField
          fullWidth
          required
          inputRef={goalFieldRef}
          size="small"
          margin="dense"
          autoFocus
          label="What were you trying to get done?"
          placeholder="e.g. Seal the 14 girders on lot 208 before Friday's ship"
          error={isValidating && goal.trim() === ''}
          value={goal}
          onChange={(event) => setGoal(event.target.value)}
        />

        <TextField
          fullWidth
          required
          multiline
          minRows={3}
          inputRef={whatHappenedFieldRef}
          size="small"
          margin="dense"
          label="What happened instead?"
          error={isValidating && whatHappened.trim() === ''}
          value={whatHappened}
          onChange={(event) => setWhatHappened(event.target.value)}
        />

        <TextField
          select
          fullWidth
          required
          size="small"
          margin="dense"
          label="What is this costing you?"
          error={isValidating && impact === ''}
          value={impact}
          onChange={(event) => setImpact(event.target.value as ProblemImpact)}
        >
          {PROBLEM_IMPACTS_WORST_FIRST.map((problemImpact) => (
            <MenuItem key={problemImpact} value={problemImpact}>
              {PROBLEM_IMPACT_LABELS[problemImpact]}
            </MenuItem>
          ))}
        </TextField>

        {screenshot && (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', marginTop: 2 }}>
            <Box
              component="img"
              src={screenshot}
              alt="Picture of your screen that will be sent with this report"
              sx={{
                width: 120,
                height: 'auto',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                opacity: includeScreenshot ? 1 : 0.4,
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeScreenshot}
                  onChange={(event) => setIncludeScreenshot(event.target.checked)}
                />
              }
              label="Include this picture of my screen"
            />
          </Stack>
        )}

        <Divider sx={{ marginTop: 2 }} />

        <Link
          component="button"
          type="button"
          underline="none"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', marginTop: 1 }}
          onClick={() => setShowCapturedContext((isShown) => !isShown)}
        >
          {showCapturedContext ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
            What else we&apos;re sending with this report
          </Typography>
        </Link>

        <Collapse in={showCapturedContext}>
          <Box
            sx={[{ marginTop: 1, maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden' }, createScrollbarStyling]}
          >
            {capturedContextRows.map(([label, value]) => (
              <Box key={label} sx={{ marginBottom: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" disabled={isSending} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" loading={isSending} loadingPosition="start" onClick={onSubmit}>
          {isSending ? 'Sending...' : 'Send Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The customer's own wall clock and timezone. Support uses this to find the matching session and server-side errors,
 * which is much harder from a UTC stamp alone when the customer says "it broke right before lunch".
 */
function describeNow() {
  const now = new Date();
  return `${now.toLocaleString()} (${Intl.DateTimeFormat().resolvedOptions().timeZone}, ${now.toISOString()} UTC)`;
}

/**
 * User-Agent Client Hints. Chromium-only, and not in TypeScript's DOM types, so it's declared narrowly here.
 */
interface UserAgentDataBrand {
  readonly brand: string;
  readonly version: string;
}

/**
 * The browser and platform in a form a support engineer can read at a glance, e.g. "Chrome 150 on Windows".
 *
 * Raw user agents are actively misleading: every Chromium browser still claims to be Mozilla *and* Safari for
 * historical reasons, so a Chrome report reads as though it came from Safari. Client Hints give clean brand and
 * platform values where they exist; everywhere else we sniff, checking the browsers that impersonate Chrome before
 * Chrome itself and Chrome before Safari, since each pretends to be the next.
 */
function describeBrowser(): string {
  const userAgent = window.navigator.userAgent;

  const userAgentData = (
    window.navigator as Navigator & {
      userAgentData?: { readonly brands?: readonly UserAgentDataBrand[]; readonly platform?: string };
    }
  ).userAgentData;

  if (userAgentData?.brands) {
    // Chromium pads this list with a deliberately meaningless entry, and reports both "Chromium" and the real brand.
    const realBrands = userAgentData.brands.filter((brand) => !/not.a.brand/i.test(brand.brand));
    const brand = realBrands.find((candidate) => candidate.brand !== 'Chromium') ?? realBrands[0];

    if (brand) {
      const name = brand.brand === 'Google Chrome' ? 'Chrome' : brand.brand;
      return userAgentData.platform
        ? `${name} ${brand.version} on ${userAgentData.platform}`
        : `${name} ${brand.version}`;
    }
  }

  const platform = /Windows/.test(userAgent)
    ? 'Windows'
    : /Macintosh|Mac OS X/.test(userAgent)
      ? 'macOS'
      : /Android/.test(userAgent)
        ? 'Android'
        : /iPhone|iPad|iPod/.test(userAgent)
          ? 'iOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null;

  const browserPatterns: readonly (readonly [string, RegExp])[] = [
    ['Edge', /Edg(?:e|A|iOS)?\/(\d+)/],
    ['Opera', /OPR\/(\d+)/],
    ['Samsung Internet', /SamsungBrowser\/(\d+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
    ['Chrome', /(?:Chrome|CriOS)\/(\d+)/],
    ['Safari', /Version\/(\d+).*Safari/],
  ];

  for (const [name, pattern] of browserPatterns) {
    const match = pattern.exec(userAgent);
    if (match) return platform ? `${name} ${match[1]} on ${platform}` : `${name} ${match[1]}`;
  }

  return platform ? `Unrecognized browser on ${platform}` : 'Unrecognized browser';
}
